/**
 * The shell: route switching and the one global affordance (error toast).
 *
 * Screens own their own layout and their own scrolling. The match screen is the
 * only one that is not scrollable, because a globe that pans when you meant to
 * spin it is a lost round.
 */

import { useState } from 'react';

import { BackgroundPlate } from './components/BackgroundPlate';
import { useRoute } from './lib/router';
import { DailyScreen } from './screens/DailyScreen';
import { CityScreen } from './screens/CityScreen';
import { FirstRunScreen } from './screens/FirstRunScreen';
import { AuthScreen } from './screens/AuthScreen';
import { BackRoadsScreen } from './screens/BackRoadsScreen';
import { PrivacyScreen, TermsScreen } from './screens/LegalScreens';
import { ProgressScreen } from './screens/ProgressScreen';
import { HomeScreen } from './screens/HomeScreen';
import { PlayScreen } from './screens/PlayScreen';
import { SignInScreen } from './screens/SignInScreen';
import { MatchScreen } from './screens/MatchScreen';
import { QueueScreen } from './screens/QueueScreen';
import { ResultsScreen } from './screens/ResultsScreen';
import { ShareLandingScreen } from './screens/ShareLandingScreen';
import { useGame } from './store/gameStore';
import { dismissIntro, hasPlayedBefore, introDismissed } from './store/storage';

function ErrorToast(): React.JSX.Element | null {
  const lastError = useGame((s) => s.lastError);
  const dismissError = useGame((s) => s.dismissError);
  if (!lastError) return null;
  return (
    <div className="gg-toast" role="alert">
      <span className="flex-1">{lastError.message}</span>
      <button type="button" className="gg-toast-close" onClick={dismissError} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}

/**
 * The home route, which is the intro on a first visit and the menu after that.
 *
 * ONLY THE HOME ROUTE. A duel link, the daily and the city route each reach a
 * first-time browser too, and every one of them is somebody who arrived to do
 * a specific thing. Intercepting those would break a shared link, so the check
 * lives here rather than as a redirect anybody could later mistake for global.
 *
 * Read ONCE, into state. `startMatch` marks the browser as having played, so a
 * live read would swap the screen out from under the player's thumb at the
 * exact moment they tapped the button on it.
 */
function HomeRoute(): React.JSX.Element {
  const [showIntro, setShowIntro] = useState(() => !hasPlayedBefore() && !introDismissed());

  if (!showIntro) return <HomeScreen />;
  return (
    <FirstRunScreen
      onSkip={() => {
        dismissIntro();
        setShowIntro(false);
      }}
    />
  );
}

/**
 * Routes that stay plain. The match screen has a globe of its own; the queue is
 * the group lobby.
 */
const NO_BACKGROUND_PLATE: ReadonlySet<string> = new Set(['match', 'queue', 'backroads']);

export function App(): React.JSX.Element {
  const { route, go } = useRoute();

  return (
    <div className="gg-app">
      {NO_BACKGROUND_PLATE.has(route.name) ? null : <BackgroundPlate />}
      {route.name === 'home' ? <HomeRoute /> : null}
      {route.name === 'play' ? <PlayScreen /> : null}
      {route.name === 'signin' ? <SignInScreen /> : null}
      {route.name === 'queue' ? <QueueScreen /> : null}
      {route.name === 'match' ? <MatchScreen /> : null}
      {route.name === 'results' ? <ResultsScreen /> : null}
      {route.name === 'daily' ? <DailyScreen /> : null}
      {route.name === 'city' ? <CityScreen tier={route.tier} /> : null}
      {route.name === 'backroads' ? <BackRoadsScreen tripId={route.tripId} /> : null}
      {route.name === 'share' ? <ShareLandingScreen token={route.token} /> : null}
      {route.name === 'auth' ? <AuthScreen provider={route.provider} /> : null}
      {route.name === 'progress' ? <ProgressScreen /> : null}
      {route.name === 'privacy' ? <PrivacyScreen /> : null}
      {route.name === 'terms' ? <TermsScreen /> : null}
      {route.name === 'not-found' ? (
        <main className="gg-screen gg-center">
          <h1 className="gg-title">Nothing here</h1>
          <button
            type="button"
            className="gg-btn gg-btn-primary mt-4"
            onClick={() => go({ name: 'home' })}
          >
            Back to the globe
          </button>
        </main>
      ) : null}
      <ErrorToast />
    </div>
  );
}
